// src/components/site-editor/panels/FooterPanel.js
import React, { useState, useEffect } from 'react';
import { Info, ArrowRight, CheckCircle, Circle } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { auth } from '../../../config/firebase-config';
import { getPolicies } from '../../../services/policies-service';

const FooterPanel = ({ onNavigate }) => {
  const { state, actions } = useWebsiteBuilder();
  const footerConfig = state.draft.contactFooter?.footerConfig || {};
  const displayName = state.draft.profile?.displayName || state.draft.profile?.fullName || '';
  const socialLinks = state.draft.contactFooter?.socialLinks || {};

  // Local state
  const [copyrightName, setCopyrightName] = useState('');
  const [layout, setLayout] = useState('simple');
  const [canonicalPolicies, setCanonicalPolicies] = useState(null);

  // Check if social links are connected
  const connectedSocials = Object.entries(socialLinks).filter(([key, value]) => value && value.trim() !== '');

  // Phase 2 read-with-fallback: prefer canonical practitioner.policies.cancellation,
  // fall back to legacy draft.contactFooter.cancellationPolicy.
  // See docs/DF-IA-Restructure-Spec-May2026.md Decision 2.
  useEffect(() => {
    const uid = auth?.currentUser?.uid;
    if (!uid) return;
    getPolicies(uid).then(setCanonicalPolicies).catch(() => {});
  }, []);

  // Check if policies exist (placeholder logic until Policies panel is built)
  const hasCancellationPolicy = !!(canonicalPolicies?.cancellation || state.draft.contactFooter?.cancellationPolicy);
  const hasPrivacyPolicy = false; // TODO: Check when Policies panel is built
  const hasTermsOfService = false; // TODO: Check when Policies panel is built

  // Load existing data on mount
  useEffect(() => {
    setCopyrightName(footerConfig.copyrightName || displayName);
    setLayout(footerConfig.layout || 'simple');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update context when fields change
  const updateFooter = (updates) => {
    const newFooterConfig = {
      copyrightName,
      layout,
      ...updates
    };
    actions.updateContactFooter('footerConfig', newFooterConfig);
  };

  const handleCopyrightNameChange = (value) => {
    setCopyrightName(value);
    updateFooter({ copyrightName: value });
  };

  const handleLayoutChange = (newLayout) => {
    setLayout(newLayout);
    updateFooter({ layout: newLayout });
  };

  const handleNavigateToPolicies = () => {
    if (onNavigate) onNavigate('policies');
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Footer Settings</h3>
        <p className="text-sm text-gray-600">
          Customize what appears at the bottom of your site—contact links, copyright, and more.
        </p>
      </div>

      {/* Inline Callout */}
      <div style={{
        padding: '12px 16px',
        background: '#EFF6FF',
        border: '1px solid #BFDBFE',
        borderRadius: '8px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start'
      }}>
        <Info size={20} style={{ color: '#3B82F6', flexShrink: 0, marginTop: '2px' }} />
        <p className="text-sm" style={{ color: '#1E40AF', lineHeight: '1.5' }}>
          Your footer is automatically built from your site content. It includes your policies, social links, and copyright. You can customize the layout and what's shown below.
        </p>
      </div>

      {/* Copyright Text */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Copyright Name
        </label>
        <input
          type="text"
          value={copyrightName}
          onChange={(e) => handleCopyrightNameChange(e.target.value)}
          placeholder="Your Name or Business Name"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
        <p className="text-xs text-gray-500">
          Preview: © {currentYear} {copyrightName || 'Your Name'}. All rights reserved.
        </p>
      </div>

      {/* Policy Links - Auto-show when created */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Policy Links
        </label>
        <p className="text-xs text-gray-600 mb-3">
          Policy links automatically appear in your footer when you create them in the Policies panel.
        </p>

        {/* Status list */}
        <div style={{
          padding: '12px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Cancellation Policy */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              {hasCancellationPolicy ? (
                <CheckCircle size={16} color="#059669" />
              ) : (
                <Circle size={16} color="#D1D5DB" />
              )}
              <span style={{ color: hasCancellationPolicy ? '#111827' : '#9CA3AF' }}>
                Cancellation Policy {!hasCancellationPolicy && '— not yet created'}
              </span>
            </div>

            {/* Privacy Policy */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              {hasPrivacyPolicy ? (
                <CheckCircle size={16} color="#059669" />
              ) : (
                <Circle size={16} color="#D1D5DB" />
              )}
              <span style={{ color: hasPrivacyPolicy ? '#111827' : '#9CA3AF' }}>
                Privacy Policy {!hasPrivacyPolicy && '— not yet created'}
              </span>
            </div>

            {/* Terms of Service */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              {hasTermsOfService ? (
                <CheckCircle size={16} color="#059669" />
              ) : (
                <Circle size={16} color="#D1D5DB" />
              )}
              <span style={{ color: hasTermsOfService ? '#111827' : '#9CA3AF' }}>
                Terms of Service {!hasTermsOfService && '— not yet created'}
              </span>
            </div>
          </div>

          {/* Link to Policies panel */}
          <button
            onClick={handleNavigateToPolicies}
            className="mt-3 text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1 transition-colors"
          >
            Set up your policies <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* Social Icons - Read Only */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Social Icons in Footer
        </label>
        <div style={{
          padding: '12px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px'
        }}>
          <p className="text-sm text-gray-600 mb-2">
            Your social media icons are managed in the Social Links panel.
          </p>
          <button
            className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1 transition-colors"
            onClick={() => { if (onNavigate) onNavigate('social'); }}
          >
            Edit Social Links <ArrowRight size={14} />
          </button>
          {connectedSocials.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Connected platforms:</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {connectedSocials.map(([platform]) => (
                  <span
                    key={platform}
                    className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {platform}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Style */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Footer Style
        </label>

        <div className="space-y-3">
          {/* Simple Layout */}
          <button
            onClick={() => handleLayoutChange('simple')}
            style={{
              width: '100%',
              padding: '16px',
              border: layout === 'simple' ? '2px solid #7c3aed' : '2px solid #E5E7EB',
              borderRadius: '8px',
              background: layout === 'simple' ? '#F3F0FF' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Simple</div>
                <div className="text-xs text-gray-600">Single centered column</div>
              </div>
              {layout === 'simple' && (
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />
                </div>
              )}
            </div>

            {/* Simple Layout Visual */}
            <div style={{
              padding: '12px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              fontSize: '9px',
              lineHeight: '1.2'
            }}>
              <div style={{ textAlign: 'center', color: '#9CA3AF' }}>
                <div style={{ marginBottom: '6px' }}>● ● ●</div>
                <div style={{ marginBottom: '6px' }}>Policy • Policy • Policy</div>
                <div style={{ marginBottom: '4px' }}>© 2026 Your Name</div>
                <div>Powered by DF</div>
              </div>
            </div>
          </button>

          {/* Detailed Layout */}
          <button
            onClick={() => handleLayoutChange('detailed')}
            style={{
              width: '100%',
              padding: '16px',
              border: layout === 'detailed' ? '2px solid #7c3aed' : '2px solid #E5E7EB',
              borderRadius: '8px',
              background: layout === 'detailed' ? '#F3F0FF' : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s'
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Detailed</div>
                <div className="text-xs text-gray-600">Three column layout</div>
              </div>
              {layout === 'detailed' && (
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />
                </div>
              )}
            </div>

            {/* Detailed Layout Visual */}
            <div style={{
              padding: '12px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              fontSize: '9px',
              lineHeight: '1.2'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#9CA3AF' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: '2px' }}>Name</div>
                  <div>Tagline</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div>Home</div>
                  <div>About</div>
                  <div>Services</div>
                </div>
                <div style={{ flex: 1, textAlign: 'right' }}>
                  <div>●</div>
                  <div>●</div>
                  <div>●</div>
                </div>
              </div>
              <div style={{ textAlign: 'center', paddingTop: '6px', borderTop: '1px solid #E5E7EB', color: '#9CA3AF' }}>
                <div>© 2026 • Powered by DF</div>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FooterPanel;
